import { useEffect, useRef } from 'react';

import { getMainPrivateApi } from 'api/apiCoreSdk';
import { displayWarningNotification } from 'components/layout/notifications/notificationsUtils';
import { addPendingDeposit } from 'reduxState/balances/pendingDeposits';
import {
  clearBankrollCode,
  setBankrollCodeApplied,
} from 'reduxState/currentUserData/userBankroll';
import { staticDispatch } from 'reduxState/store';
import { useSelector } from 'reduxState/storeCore';

export function useAutomaticallyApplyBankrollCode(
  bankrollCode: string | undefined,
  amount: string | undefined,
  isValid: boolean
) {
  /** Run apply only once per session (fixes local dev runs) */
  const applyingRef = useRef(false);

  const walletAddress = useSelector(state => state.wallet.walletAddress);
  const ready = walletAddress && bankrollCode && isValid;
  useEffect(() => {
    if (!ready || applyingRef.current) {
      return;
    }
    applyingRef.current = true;
    console.log('[BankrollWorker] Applying code');
    getMainPrivateApi()
      .redeemBankroll({
        code: bankrollCode,
        wallet: walletAddress,
      })
      .then(response => {
        staticDispatch(setBankrollCodeApplied());
        if (amount) {
          staticDispatch(
            addPendingDeposit({
              quantity: `${amount}`,
              sourceChain: 'katanaChain',
              sourceTxId: response.txHash,
              vault: undefined,
            })
          );
        }
      })
      .catch(error => {
        console.warn(error);
        // TODO: error message Copy
        displayWarningNotification(
          `Unable to apply promo code. Reason: ${error?.message}`
        );
        // Remove code on failure - prevents refresh re-try (on broken code)
        staticDispatch(clearBankrollCode());
      });
  }, [amount, bankrollCode, isValid, ready, walletAddress]);

  return {
    applying: ready,
  };
}

export const DEFAULT_WELCOME_BONUS_MESSAGE =
  'Complete one trade to earn a points bonus. Share your referral code with others to earn more points and a percentage of their trading fees.';

export function useAutomaticallyApplyPointsBankrollCode(
  bankrollCode: string | undefined,
  isValid: boolean
) {
  /** Run apply only once per session (fixes local dev runs) */
  const applyingRef = useRef(false);

  const walletAddress = useSelector(state => state.wallet.walletAddress);
  const ready = walletAddress && bankrollCode && isValid;
  useEffect(() => {
    if (!ready || applyingRef.current) {
      return;
    }
    applyingRef.current = true;
    console.log('[BankrollWorker] Applying points code');
    getMainPrivateApi()
      .redeemWelcomeBonus({
        code: bankrollCode,
        wallet: walletAddress,
      })
      .then(response => {
        staticDispatch(
          setBankrollCodeApplied(
            response.message || DEFAULT_WELCOME_BONUS_MESSAGE
          )
        );
      })
      .catch(error => {
        console.warn(error);
        // TODO: error message Copy
        displayWarningNotification(
          `Unable to apply promo code. Reason: ${error?.message}`
        );
        // Remove code on failure - prevents refresh re-try (on broken code)
        staticDispatch(clearBankrollCode());
      });
  }, [bankrollCode, isValid, ready, walletAddress]);

  return {
    applying: ready,
  };
}
